import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import TrueFlowLogo from "../components/TrueFlowLogo";
import { useAuth } from "../context/AuthContext";
import { refActual } from "../lib/referral";
import { api } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "../components/ui/alert";
import logger from "../lib/logger";

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 23">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

// Google logo SVG
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(null); // 'microsoft' | 'google' | null
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");

  const handleSubmit = async (e) => {
    e.preventDefault();
    logger.debug("[Login] handleSubmit: START");
    if (!email || !password) {
      logger.debug("[Login] handleSubmit: Missing email or password");
      toast.error(t("common.required"));
      return;
    }

    logger.debug("[Login] handleSubmit: Setting loading=true");
    setLoading(true);
    try {
      logger.debug("[Login] handleSubmit: Calling login()");
      await login(email, password);
      logger.debug("[Login] handleSubmit: login() completed successfully");
      toast.success(t("auth.welcomeBack"));
      logger.debug("[Login] handleSubmit: Navigating to /dashboard");
      navigate("/dashboard");
      logger.debug("[Login] handleSubmit: Navigation initiated");
    } catch (error) {
      logger.error("[Login] handleSubmit: ERROR", error);
      toast.error(error.response?.data?.detail || t("errors.invalidCredentials"));
    } finally {
      logger.debug("[Login] handleSubmit: Setting loading=false");
      setLoading(false);
    }
    logger.debug("[Login] handleSubmit: END");
  };

  const handleMicrosoftLogin = async () => {
    setSsoLoading("microsoft");
    try {
      // SSO también es una puerta de ALTA: un usuario nuevo crea tenant en el
      // callback. El ref viaja como query y el backend lo mete en el `state`
      // de OAuth, que el proveedor devuelve tal cual.
      const ref = refActual();
      const response = await api.get("/auth/microsoft/login",
        ref ? { params: { ref } } : undefined);
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(t("auth.microsoftLoginError"));
      setSsoLoading(null);
    }
  };

  const handleGoogleLogin = async () => {
    setSsoLoading("google");
    try {
      const ref = refActual();
      const response = await api.get("/auth/google/login",
        ref ? { params: { ref } } : undefined);
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(t("auth.googleLoginError"));
      setSsoLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/">
            <TrueFlowLogo variant="light" width={440} height={120} />
          </Link>
        </div>

        <Card className="shadow-sm" data-testid="login-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">{t("auth.login")}</CardTitle>
            <CardDescription>{t("auth.loginSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Error Alert */}
            {errorParam && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {decodeURIComponent(errorParam)}
                </AlertDescription>
              </Alert>
            )}

            {/* SSO Buttons */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 gap-3"
                onClick={handleMicrosoftLogin}
                disabled={ssoLoading !== null}
                data-testid="microsoft-login-btn"
              >
                {ssoLoading === "microsoft" ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <MicrosoftLogo />
                )}
                {t("auth.continueWithMicrosoft")}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11 gap-3"
                onClick={handleGoogleLogin}
                disabled={ssoLoading !== null}
                data-testid="google-login-btn"
              >
                {ssoLoading === "google" ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <GoogleLogo />
                )}
                {t("auth.continueWithGoogle")}
              </Button>
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {t("auth.continueWithEmail")}
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading || ssoLoading !== null} data-testid="login-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("common.loading")}
                  </>
                ) : (
                  t("auth.login")
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              {t("auth.noAccount")}{" "}
              <Link to="/register" className="text-primary hover:underline font-medium">
                {t("auth.register")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
